// types
import {
	AccountEntityRelationship,
	ActivityType,
	ArticleCategory,
	EntitySubtype,
	EntityType,
	ErrorCause,
	FormName,
	PermissionResource,
	Status,
	TransactionStatus,
	TransactionType,
	UserRole,
} from '../@types';

// i18
import { TFunction } from 'i18next';

export const transactionTooltipKeys: Record<TransactionType, string> = {
	ADVANCE: 'advance',
	CASH_BENEFIT: 'cashBenefit',
	DIRECT: 'directPayment',
	INITIAL: 'initialDeposit',
	NON_CASH_BENEFIT: 'nonCashBenefit',
	OPEX: 'operatingExpense',
	PAYMENT: 'payment',
	PAYMENT_REFUND: 'paymentRefund',
	REIMBURSEMENT: 'reimbursement',
	REPLENISHMENT: 'inflow',
	SPECIAL: 'specialCommitment',
	TRANSFER: 'transfer',
};

export const transactionReceivedTooltipKeys: Partial<Record<TransactionType, string>> = {
	CASH_BENEFIT: 'cashBenefitReceived',
	PAYMENT: 'paymentReceived',
};

export const transactionNotReceivedTooltipKeys: Partial<Record<TransactionType, string>> = {
	CASH_BENEFIT: 'cashBenefitNotReceived',
	PAYMENT: 'paymentNotReceived',
};

export const transactionRevertedTooltipKeys: Record<TransactionType, string> = {
	ADVANCE: 'advanceReverted',
	CASH_BENEFIT: 'cashBenefitReverted',
	DIRECT: 'directPaymentReverted',
	INITIAL: 'initialDepositReverted',
	NON_CASH_BENEFIT: 'nonCashBenefitReverted',
	OPEX: 'operatingExpenseReverted',
	PAYMENT: 'paymentReverted',
	PAYMENT_REFUND: 'paymentRefundReverted',
	REIMBURSEMENT: 'reimbursementReverted',
	REPLENISHMENT: 'replenishmentReverted',
	SPECIAL: 'specialCommitmentReverted',
	TRANSFER: 'transferReverted',
};

export const translateActivityType = (type: ActivityType, t: TFunction) => {
	const activityTypeMap: Record<ActivityType, string> = {
		ACCOUNT_CREATED: t('activityTypes.accountCreated'),
		ACCOUNT_UPDATED: t('activityTypes.accountUpdated'),
		ACT_CREATED: t('activityTypes.actCreated'),
		ACT_UPDATED: t('activityTypes.actUpdated'),
		AI_ACTION_TRIGGERED: t('activityTypes.aiActionTriggered'),
		AI_CONDITION_MATCH_REVIEWED: t('activityTypes.aiConditionMatchReviewed'),
		AI_DOCUMENT_PROCESSED: t('activityTypes.aiDocumentProcessed'),
		AI_DOCUMENT_UPLOADED: t('activityTypes.aiDocumentUploaded'),
		ALLOCATION_ALLOCATED: t('activityTypes.allocationAllocated'),
		ALLOCATION_LOCKED: t('activityTypes.allocationLocked'),
		ALLOCATION_REFUNDED: t('activityTypes.allocationRefunded'),
		BENEFIT_CREATED: t('activityTypes.benefitCreated'),
		BENEFIT_DELETED: t('activityTypes.benefitDeleted'),
		BENEFIT_UPDATED: t('activityTypes.benefitUpdated'),
		CASH_IN_METHOD_ACTIVATED: t('activityTypes.cashInMethodActivated'),
		CASH_IN_METHOD_CREATED: t('activityTypes.cashInMethodCreated'),
		CASH_IN_METHOD_DEACTIVATED: t('activityTypes.cashInMethodDeactivated'),
		CASH_IN_METHOD_DELETED: t('activityTypes.cashInMethodDeleted'),
		CASH_IN_METHOD_UPDATED: t('activityTypes.cashInMethodUpdated'),
		CATEGORY_ACTIVATED: t('activityTypes.categoryActivated'),
		CATEGORY_CREATED: t('activityTypes.categoryCreated'),
		CATEGORY_DEACTIVATED: t('activityTypes.categoryDeactivated'),
		CATEGORY_UPDATED: t('activityTypes.categoryUpdated'),
		COMMENT_CREATED: t('activityTypes.commentCreated'),
		COMPONENT_ACTIVATED: t('activityTypes.componentActivated'),
		COMPONENT_CREATED: t('activityTypes.componentCreated'),
		COMPONENT_DEACTIVATED: t('activityTypes.componentDeactivated'),
		COMPONENT_UPDATED: t('activityTypes.componentUpdated'),
		CONDITION_CREATED: t('activityTypes.conditionCreated'),
		CONDITION_DEACTIVATED: t('activityTypes.conditionDeactivated'),
		CONDITION_DELETED: t('activityTypes.conditionDeleted'),
		CONDITION_SATISFIED: t('activityTypes.conditionSatisfied'),
		CONDITION_UPDATED: t('activityTypes.conditionUpdated'),
		CONDITION_VERIFIED: t('activityTypes.conditionVerified'),
		CONTRACT_APPROVED: t('activityTypes.contractApproved'),
		CONTRACT_CANCELLED: t('activityTypes.contractCancelled'),
		CONTRACT_COMPLETED: t('activityTypes.contractCompleted'),
		CONTRACT_CREATED: t('activityTypes.contractCreated'),
		CONTRACT_DEACTIVATED: t('activityTypes.contractDeactivated'),
		CONTRACT_DELETED: t('activityTypes.contractDeleted'),
		CONTRACT_REOPENED: t('activityTypes.contractReopened'),
		CONTRACT_RETURNED: t('activityTypes.contractReturned'),
		CONTRACT_SUBMITTED: t('activityTypes.contractSubmitted'),
		CONTRACT_UPDATED: t('activityTypes.contractUpdated'),
		CUSTOM_FIELD_DEACTIVATED: t('activityTypes.customFieldDeactivated'),
		DOCUMENT_CREATED: t('activityTypes.documentCreated'),
		DOCUMENT_TYPE_CREATED: t('activityTypes.documentTypeCreated'),
		DOCUMENT_TYPE_UPDATED: t('activityTypes.documentTypeUpdated'),
		DOCUMENT_UPDATED: t('activityTypes.documentUpdated'),
		ENTITY_CREATED: t('activityTypes.entityCreated'),
		ENTITY_UPDATED: t('activityTypes.entityUpdated'),
		ENTITY_WALLET_ACTIVATED: t('activityTypes.entityWalletActivated'),
		ENTITY_WALLET_DEACTIVATED: t('activityTypes.entityWalletDeactivated'),
		EXCEPTION_CREATED: t('activityTypes.exceptionCreated'),
		EXCEPTION_DEACTIVATED: t('activityTypes.exceptionDeactivated'),
		EXCEPTION_DELETED: t('activityTypes.exceptionDeleted'),
		EXCEPTION_NOTIFICATION_SENT: t('activityTypes.exceptionNotificationSent'),
		EXCEPTION_OVERRIDDEN: t('activityTypes.exceptionOverridden'),
		EXPENSE_TYPE_ACTIVATED: t('activityTypes.expenseTypeActivated'),
		EXPENSE_TYPE_CREATED: t('activityTypes.expenseTypeCreated'),
		EXPENSE_TYPE_DEACTIVATED: t('activityTypes.expenseTypeDeactivated'),
		EXPENSE_TYPE_UPDATED: t('activityTypes.expenseTypeUpdated'),
		FORM_CREATED: t('activityTypes.formCreated'),
		FORM_UPDATED: t('activityTypes.formUpdated'),
		FUNDING_BURNED: t('activityTypes.fundingBurned'),
		FUNDING_MINTED: t('activityTypes.fundingMinted'),
		IFR_CREATED: t('activityTypes.ifrCreated'),
		IFR_FINALIZED: t('activityTypes.ifrFinalized'),
		IFR_PLAN_CREATED: t('activityTypes.ifrPlanCreated'),
		IFR_PLAN_UPDATED: t('activityTypes.ifrPlanUpdated'),
		IFR_PLAN_VERSION_APPROVED: t('activityTypes.ifrPlanVersionApproved'),
		IFR_PLAN_VERSION_CREATED: t('activityTypes.ifrPlanVersionCreated'),
		IFR_PLAN_VERSION_FINALIZED: t('activityTypes.ifrPlanVersionFinalized'),
		IFR_PLAN_VERSION_REJECTED: t('activityTypes.ifrPlanVersionRejected'),
		IFR_PLAN_VERSION_UPDATED: t('activityTypes.ifrPlanVersionUpdated'),
		IFR_UPDATED: t('activityTypes.ifrUpdated'),
		ILAP: t('activityTypes.ilap'),
		INVOICE_AI_EXTRACTED: t('activityTypes.invoiceAiExtracted'),
		INVOICE_APPROVED: t('activityTypes.invoiceApproved'),
		INVOICE_CREATED: t('activityTypes.invoiceCreated'),
		INVOICE_DEACTIVATED: t('activityTypes.invoiceDeactivated'),
		INVOICE_DELETED: t('activityTypes.invoiceDeleted'),
		INVOICE_DOCUMENT_UPLOADED: t('activityTypes.invoiceDocumentUploaded'),
		INVOICE_PAID: t('activityTypes.invoicePaid'),
		INVOICE_PAYMENT_STARTED: t('activityTypes.invoicePaymentStarted'),
		INVOICE_PUT_ON_HOLD: t('activityTypes.invoicePutOnHold'),
		INVOICE_REJECTED: t('activityTypes.invoiceRejected'),
		INVOICE_REOPENED: t('activityTypes.invoiceReopened'),
		INVOICE_RESUMED: t('activityTypes.invoiceResumed'),
		INVOICE_SUBMITTED: t('activityTypes.invoiceSubmitted'),
		INVOICE_SUPPLIER_CONFIRMED: t('activityTypes.invoiceSupplierConfirmed'),
		INVOICE_UPDATED: t('activityTypes.invoiceUpdated'),
		LOAN_CREATED: t('activityTypes.loanCreated'),
		LOAN_ENABLED: t('activityTypes.loanEnabled'),
		LOAN_UPDATED: t('activityTypes.loanUpdated'),
		PAYMENT_CREATED: t('activityTypes.paymentCreated'),
		PAYMENT_STANDALONE_CREATED: t('activityTypes.paymentStandaloneCreated'),
		PAYMENT_STANDALONE_REDEEMED: t('activityTypes.paymentStandaloneRedeemed'),
		PLANNING_ACTIVITY_CREATED: t('activityTypes.planningActivityCreated'),
		PLANNING_ACTIVITY_DEACTIVATED: t('activityTypes.planningActivityDeactivated'),
		PLANNING_ACTIVITY_DELETED: t('activityTypes.planningActivityDeleted'),
		PLANNING_ACTIVITY_UPDATED: t('activityTypes.planningActivityUpdated'),
		PROGRAM_CREATED: t('activityTypes.programCreated'),
		PROGRAM_UPDATED: t('activityTypes.programUpdated'),
		PROGRESS_ENTRY_DEACTIVATED: t('activityTypes.progressEntryDeactivated'),
		PROGRESS_ENTRY_REPORTED: t('activityTypes.progressEntryReported'),
		PROJECT_CREATED: t('activityTypes.projectCreated'),
		PROJECT_UPDATED: t('activityTypes.projectUpdated'),
		RECONCILIATION_ITEM_ACTIVATED: t('activityTypes.reconciliationItemActivated'),
		RECONCILIATION_ITEM_CREATED: t('activityTypes.reconciliationItemCreated'),
		RECONCILIATION_ITEM_DEACTIVATED: t('activityTypes.reconciliationItemDeactivated'),
		RECONCILIATION_ITEM_UPDATED: t('activityTypes.reconciliationItemUpdated'),
		SPEND_PLAN_ACTIVITY_ALLOCATION_CREATED: t('activityTypes.spendPlanActivityAllocationCreated'),
		SPEND_PLAN_ACTIVITY_ALLOCATION_DELETED: t('activityTypes.spendPlanActivityAllocationDeleted'),
		SPEND_PLAN_ACTIVITY_ALLOCATION_UPDATED: t('activityTypes.spendPlanActivityAllocationUpdated'),
		SPEND_PLAN_ALLOCATION_DEACTIVATED: t('activityTypes.spendPlanAllocationDeactivated'),
		SPEND_PLAN_ALLOCATION_UPSERTED: t('activityTypes.spendPlanAllocationUpserted'),
		SPEND_PLAN_APPROVED: t('activityTypes.spendPlanApproved'),
		SPEND_PLAN_BLOCKCHAIN_ACTIVATED: t('activityTypes.spendPlanBlockchainActivated'),
		SPEND_PLAN_BLOCKCHAIN_CREATED: t('activityTypes.spendPlanBlockchainCreated'),
		SPEND_PLAN_COMPLETED: t('activityTypes.spendPlanCompleted'),
		SPEND_PLAN_CONTRACT_ALLOCATION_CREATED: t('activityTypes.spendPlanContractAllocationCreated'),
		SPEND_PLAN_CONTRACT_ALLOCATION_DELETED: t('activityTypes.spendPlanContractAllocationDeleted'),
		SPEND_PLAN_CONTRACT_ALLOCATION_UPDATED: t('activityTypes.spendPlanContractAllocationUpdated'),
		SPEND_PLAN_CREATED: t('activityTypes.spendPlanCreated'),
		SPEND_PLAN_DEACTIVATED: t('activityTypes.spendPlanDeactivated'),
		SPEND_PLAN_DELETED: t('activityTypes.spendPlanDeleted'),
		SPEND_PLAN_REJECTED: t('activityTypes.spendPlanRejected'),
		SPEND_PLAN_REOPENED: t('activityTypes.spendPlanReopened'),
		SPEND_PLAN_RETURNED: t('activityTypes.spendPlanReturned'),
		SPEND_PLAN_SUBMITTED: t('activityTypes.spendPlanSubmitted'),
		SPEND_PLAN_UPDATED: t('activityTypes.spendPlanUpdated'),
		SPEND_PLAN_WORK_PLAN_LINKED: t('activityTypes.spendPlanWorkPlanLinked'),
		SPREADSHEET_DEACTIVATED: t('activityTypes.spreadsheetDeactivated'),
		SPREADSHEET_DELETED: t('activityTypes.spreadsheetDeleted'),
		SPREADSHEET_UPDATED: t('activityTypes.spreadsheetUpdated'),
		SPREADSHEET_UPLOADED: t('activityTypes.spreadsheetUploaded'),
		TRANSACTION_CONFIRMED: t('activityTypes.transactionConfirmed'),
		TRANSACTION_CREATED: t('activityTypes.transactionCreated'),
		TRANSACTION_RETRIED: t('activityTypes.transactionRetried'),
		TRANSACTION_REVERTED: t('activityTypes.transactionReverted'),
		TRANSACTION_UPDATED: t('activityTypes.transactionUpdated'),
		UPLOAD_TEMPLATE_CREATED: t('activityTypes.uploadTemplateCreated'),
		UPLOAD_TEMPLATE_DEACTIVATED: t('activityTypes.uploadTemplateDeactivated'),
		UPLOAD_TEMPLATE_UPDATED: t('activityTypes.uploadTemplateUpdated'),
		USER_ADDED: t('activityTypes.userAdded'),
		USER_CREATED: t('activityTypes.userCreated'),
		USER_REMOVED: t('activityTypes.userRemoved'),
		USER_UPDATED: t('activityTypes.userUpdated'),
		WITHDRAWAL_APPLICATION_CREATED: t('activityTypes.withdrawalApplicationCreated'),
		WITHDRAWAL_APPLICATION_UPDATED: t('activityTypes.withdrawalApplicationUpdated'),
		VERIFICATION_GROUP_CREATED: t('activityTypes.verificationGroupCreated'),
		VERIFICATION_GROUP_DEACTIVATED: t('activityTypes.verificationGroupDeactivated'),
		VOUCHER_APPROVED: t('activityTypes.voucherApproved'),
		VOUCHER_CANCELLED: t('activityTypes.voucherCancelled'),
		VOUCHER_CREATED: t('activityTypes.voucherCreated'),
		VOUCHER_DEACTIVATED: t('activityTypes.voucherDeactivated'),
		VOUCHER_DELETED: t('activityTypes.voucherDeleted'),
		VOUCHER_FAILED: t('activityTypes.voucherFailed'),
		VOUCHER_MINTED: t('activityTypes.voucherMinted'),
		VOUCHER_REDEEMED: t('activityTypes.voucherRedeemed'),
		VOUCHER_REJECTED: t('activityTypes.voucherRejected'),
		VOUCHER_UPDATED: t('activityTypes.voucherUpdated'),
		VOUCHER_VERIFIED: t('activityTypes.voucherVerified'),
		WORK_PLAN_APPROVED: t('activityTypes.workPlanApproved'),
		WORK_PLAN_COMPLETED: t('activityTypes.workPlanCompleted'),
		WORK_PLAN_CREATED: t('activityTypes.workPlanCreated'),
		WORK_PLAN_DEACTIVATED: t('activityTypes.workPlanDeactivated'),
		WORK_PLAN_DELETED: t('activityTypes.workPlanDeleted'),
		WORK_PLAN_LOCKED: t('activityTypes.workPlanLocked'),
		WORK_PLAN_REJECTED: t('activityTypes.workPlanRejected'),
		WORK_PLAN_REOPENED: t('activityTypes.workPlanReopened'),
		WORK_PLAN_RETURNED: t('activityTypes.workPlanReturned'),
		WORK_PLAN_SUBMITTED: t('activityTypes.workPlanSubmitted'),
		WORK_PLAN_UPDATED: t('activityTypes.workPlanUpdated'),
	};

	return activityTypeMap[type] || type;
};

export interface TranslateErrorProps {
	cause: Partial<ErrorCause>;
	t: TFunction;
	frame?: 'SUPPLIER' | 'BENEFICIARY';
}

export const translateError = ({ cause, t, frame }: TranslateErrorProps) => {
	const errorMap: Record<ErrorCause['id'], string> = {
		ACCOUNT_BALANCES_NON_EXISTENT: t('error.accountBalancesNonExistent'),
		ACCOUNT_DUPLICATE: t('error.accountDuplicate'),
		ACCOUNT_ID_NON_EXISTENT: t('error.accountIdNonExistent'),
		ACCOUNT_INVALID_ENTITY_TRANSACTIONS: t('error.accountEntityTransactions', {
			entity: cause?.details?.entity,
		}),
		ACCOUNT_INVALID_RELATIONSHIP: t('error.default'),
		ACCOUNT_INVALID_TYPE: t('error.default'),
		ACCOUNT_NUMBER_DUPLICATE: t('error.accountNumberDuplicate', { num: cause?.details?.num }),
		ACCOUNT_NON_EXISTENT: t('error.accountNonExistent', { id: cause?.details?.id }),
		ACCOUNT_OWNER_ASSOCIATE: t('error.accountOwnerAssociate'),
		ACCOUNT_OWNER_NON_EXISTENT: t('error.default'),
		ACCOUNT_PROJECT_NON_EXISTENT: t('error.accountProjectNonExistent'),
		ACCOUNT_UNAUTHORIZED_GET: t('error.unauthorized'),
		ACCOUNT_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		ACCOUNT_UNAUTHORIZED_POST: t('error.unauthorized'),
		ACT_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		ACT_UNAUTHORIZED_POST: t('error.unauthorized'),
		ACTIVITY_INVALID_TYPE: t('error.default'),
		ACTIVITY_UNAUTHORIZED_GET: t('error.unauthorized'),
		B2B_USER_FAILED: t('error.b2bUserFailed'),
		B2B_USER_NOT_PERMITTED: t('error.b2bUserNotPermitted'),
		BENEFIT_NON_EXISTENT: t('error.default'),
		BENEFIT_UNAUTHORIZED_POST: t('error.unauthorized'),
		BLOCKCHAIN_TRANSACTION_FAILED: t('error.default'),
		BULK_UPLOAD_FIELD_EMPTY: t('error.bulkUploadFieldEmpty'),
		BULK_UPLOAD_INCOMPLETE: t('error.default'),
		CASH_IN_METHOD_DEFAULT_CANNOT_DELETE: t('error.default'),
		CASH_IN_METHOD_DEFAULT_CANNOT_UPDATE: t('error.default'),
		CASH_IN_METHOD_NOT_FOUND: t('error.default'),
		CASH_IN_METHOD_UNAUTHORIZED: t('error.unauthorized'),
		CATEGORY_ALLOCATION_CURRENCY_REQUIRED: t('error.allocationCurrencyRequired'),
		CATEGORY_ALLOCATION_EXCEEDS_LOAN_AMOUNT: t('error.allocationExceedsLoanAmount'),
		CATEGORY_ALLOCATION_INVALID_AMOUNT: t('error.allocationInvalidAmount'),
		CATEGORY_ALLOCATION_LOAN_NOT_FOUND: t('error.allocationLoanNotFound'),
		CATEGORY_ALLOCATION_WBG_LOAN_AMOUNT_NOT_FOUND: t('error.allocationLoanNotFound'),
		CATEGORY_DEFAULT_NOT_EDITABLE: t('error.categoryDefaultNotEditable'),
		CATEGORY_NON_EXISTENT: t('error.categoryNonExistent'),
		CATEGORY_NOT_FOUND: t('error.categoryNonExistent'),
		CATEGORY_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		CATEGORY_UNAUTHORIZED_POST: t('error.unauthorized'),
		COMMENT_UNAUTHORIZED_POST: t('error.unauthorized'),
		COMPONENT_ALLOCATION_CURRENCY_REQUIRED: t('error.allocationCurrencyRequired'),
		COMPONENT_ALLOCATION_EXCEEDS_LOAN_AMOUNT: t('error.allocationExceedsLoanAmount'),
		COMPONENT_ALLOCATION_INVALID_AMOUNT: t('error.allocationInvalidAmount'),
		COMPONENT_ALLOCATION_LOAN_NOT_FOUND: t('error.allocationLoanNotFound'),
		COMPONENT_NON_EXISTENT: t('error.componentNonExistent'),
		COMPONENT_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		COMPONENT_UNAUTHORIZED_POST: t('error.unauthorized'),
		CONTRACT_COMMENT_REQUIRED: t('error.contractCommentRequired'),
		CONTRACT_DUPLICATE: t('error.contractDuplicate', { num: cause?.details?.num }),
		CONTRACT_ENTITIES_NON_EXISTENT: t('error.contractSupplierEmpty'),
		CONTRACT_ID_NON_EXISTENT: t('error.contractIdNonExistent'),
		CONTRACT_NON_EXISTENT: t('error.contractNonExistent', { id: cause?.details?.id }),
		CONTRACT_UNAUTHORIZED_GET: t('error.unauthorized'),
		CONTRACT_UNAUTHORIZED_POST: t('error.unauthorized'),
		CONTRACT_MAKER_CHECKER_VIOLATION: t('error.contractMakerCheckerViolation'),
		COUNTRY_CODE_DUPLICATE: t('error.default'),
		COUNTRY_CODE_INVALID: t('error.countryCodeInvalid'),
		COUNTRY_DUPLICATE: t('error.default'),
		CRM_CREATE_ANNOTATION_ERROR: t('error.crmCreateAnnotationFailed'),
		CRM_CREATE_CONTACT_ERROR: t('error.crmCreateContactFailed'),
		CRM_CREATE_INCIDENT_ERROR: t('error.crmCreateIncidentFailed'),
		CRM_GENERATE_TOKEN_ERROR: t('error.crmGenerateAccessTokenFailed'),
		CSV_EMPTY_OR_INVALID: t('error.csvFormatInvalid'),
		DESIGNATED_ACCOUNT_HAS_TRANSACTIONS: t('error.designatedAccountHasTransactions'),
		DOCUMENT_CONTRACT_NON_EXISTENT: t('error.documentContractNonExistent'),
		DOCUMENT_FORBIDDEN: t('error.documentForbidden'),
		DOCUMENT_DELETE_FAILED: t('error.documentDeleteFailed'),
		DOCUMENT_FILE_NON_EXISTENT: t('error.documentFileNonExistent'),
		DOCUMENT_INSERT_FAIL: t('error.server'),
		DOCUMENT_INVALID_CONTENT_TYPE: t('error.documentInvalidMimeType'),
		DOCUMENT_INVALID_MIME_TYPE: t('error.documentInvalidMimeType'),
		DOCUMENT_TRANSACTION_NON_EXISTENT: t('error.documentTransactionNonExistent'),
		DOCUMENT_TYPE_DUPLICATE: t('error.documentTypeDuplicate', { name: cause?.details?.name }),
		DOCUMENT_TYPE_NAME_NON_EXISTENT: t('error.documentTypeNameNonExistent'),
		DOCUMENT_TYPE_NON_EXISTENT: t('error.documentTypeNonExistent'),
		DOCUMENT_TYPE_UNAUTHORIZED_POST: t('error.unauthorized'),
		DOCUMENT_UNAUTHORIZED_GET: t('error.unauthorized'),
		DOCUMENT_UNAUTHORIZED_POST: t('error.unauthorized'),
		ENTITY_DUPLICATE: t('error.entityDuplicate', { id: cause?.details?.idExternal }),
		ENTITY_DUPLICATE_ID_EXTERNAL: t('error.entityDuplicate'),
		ENTITY_INVALID_TYPE: t('error.entityInvalidType', { type: cause?.details?.type }),
		ENTITY_KYC_UNVERIFIED: t('error.default'),
		ENTITY_LOANS_IDENTICAL: t('error.entityLoansIdentical'),
		ENTITY_PARENT_NON_EXISTENT: t('error.associatedEntityNotSelected'),
		ENTITY_UNAUTHORIZED_GET: t('error.unauthorized'),
		ENTITY_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		ENTITY_UNAUTHORIZED_POST: t('error.unauthorized'),
		ENTITY_WALLET_NON_EXISTENT: t('error.entityWalletNonExistent'),
		ENTITY_WALLET_RELATION_NON_EXISTENT: t('error.entityWalletNonExistent'),
		EXPENSE_TYPE_DUPLICATE: t('error.expenseTypeDuplicate', { name: cause?.details?.name }),
		EXPENSE_TYPE_NAME_NON_EXISTENT: t('error.expenseTypeNameNonExistent'),
		EXPENSE_TYPE_NON_EXISTENT: t('error.expenseTypeNonExistent'),
		EXPENSE_TYPE_UNAUTHORIZED_POST: t('error.unauthorized'),
		EXPENSE_TYPE_PATCH_FAILED: t('error.expenseTypeUpdateFailed'),
		EXPENSE_TYPE_REQUIRED_DOCUMENT_MISSING: t('error.transactionDocumentsRequiredTypesMissing', {
			expenseType: cause?.details?.expenseType,
		}),
		EXPORT_NO_DATA: t('error.default'),
		FILE_INVALID_FORMAT: t('error.fileInvalidFormat'),
		FINANCING_SOURCE_NOT_SELECTED: t('error.financingSourceNotSelected'),
		FORM_FIELD_CHECKBOX_INVALID: t('error.formFieldCheckboxInvalid', {
			label: cause?.details?.label,
			options: cause?.details?.options,
		}),
		FORM_FIELD_DATE_INVALID: t('error.formFieldDateInvalid', { label: cause?.details?.label }),
		FORM_FIELD_LABEL_DUPLICATE: t('error.formFieldLabelDuplicate'),
		FORM_FIELD_NUMBER_INVALID: t('error.formFieldNumberInvalid', { label: cause?.details?.label }),
		FORM_FIELD_SELECT_INVALID: t('error.formFieldSelectInvalid', {
			label: cause?.details?.label,
			options: cause?.details?.options,
		}),
		GRAPH_UNAUTHORIZED: t('error.graphUnauthorized'),
		GRAPH_USER_NOT_FOUND: t('error.default'),
		ID_NON_EXISTENT: t('error.idNonExistent'),
		IFR_INVALID_STATUS: t('error.ifrInvalidStatus'),
		IFR_PLAN_CASH_IN_EXCEEDS_LOAN_AMOUNT: t('error.planCashInExceedsLoanAmount'),
		IFR_PLAN_CASH_OUT_CATEGORY_MONTH_EXCEEDS_CASH_IN: t('error.planCashOutCategoryExceedsCashIn'),
		IFR_PLAN_CASH_OUT_COMPONENT_MONTH_EXCEEDS_CASH_IN: t('error.planCashOutComponentExceedsCashIn'),
		IFR_PLAN_CATEGORY_ALLOCATION_EXCEEDED: t('error.planCategoryAllocationExceeded'),
		IFR_PLAN_COMPONENT_ALLOCATION_EXCEEDED: t('error.planComponentAllocationExceeded'),
		IFR_PLAN_DUPLICATE: t('error.ifrPlanDuplicate'),
		IFR_PLAN_FISCAL_YEAR_DUPLICATE: t('error.ifrPlanFiscalYearDuplicate'),
		IFR_PLAN_FISCAL_YEAR_END_NON_EXISTENT: t('error.default'),
		IFR_PLAN_FISCAL_YEAR_START_NON_EXISTENT: t('error.default'),
		IFR_PLAN_INSERT_FAIL: t('error.server'),
		IFR_PLAN_NAME_NON_EXISTENT: t('error.default'),
		IFR_PLAN_NOT_FOUND: t('error.ifrPlanNotFound'),
		IFR_PLAN_PROJECT_NON_EXISTENT: t('error.default'),
		IFR_PLAN_UNAUTHORIZED_GET: t('error.unauthorized'),
		IFR_PLAN_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		IFR_PLAN_UNAUTHORIZED_POST: t('error.unauthorized'),
		IFR_PLAN_VERSION_DATA_DUPLICATE: t('error.default'),
		IFR_PLAN_VERSION_DATA_IMMUTABLE: t('error.ifrPlanVersionDataImmutable'),
		IFR_PLAN_VERSION_DATA_INVALID_FOREIGN_KEY: t('error.ifrPlanVersionDataInvalidForeignKey'),
		IFR_PLAN_VERSION_DATA_INVALID_MONTH: t('error.ifrPlanVersionDataInvalidMonth'),
		IFR_PLAN_VERSION_DATA_INVALID_SINGLE_REFERENCE: t(
			'error.ifrPlanVersionDataInvalidSingleReference'
		),
		IFR_PLAN_VERSION_DATA_LOAN_NON_EXISTENT: t('error.default'),
		IFR_PLAN_VERSION_DRAFT_EXISTS: t('error.ifrPlanVersionDraftExists'),
		IFR_PLAN_VERSION_INSERT_FAIL: t('error.server'),
		IFR_PLAN_VERSION_INVALID_TRANSITION: t('error.default'),
		IFR_PLAN_VERSION_NOT_FINALIZED: t('error.ifrPlanNotFinalized'),
		IFR_PLAN_VERSION_NOT_FOUND: t('error.ifrPlanNotFound'),
		IFR_PLAN_VERSION_PLAN_NON_EXISTENT: t('error.ifrPlanNotFound'),
		IFR_PLAN_VERSION_UNAUTHORIZED_GET: t('error.unauthorized'),
		IFR_PLAN_VERSION_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		IFR_PLAN_VERSION_UNAUTHORIZED_POST: t('error.unauthorized'),
		IFR_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		IFR_UNAUTHORIZED_POST: t('error.unauthorized'),
		INVALID_COLUMN: t('error.default'),
		LOAN_CURRENCY_NOT_FOUND: t('error.loanCurrencyNotFound'),
		LOAN_DISBURSED_GREATER_THAN_AMOUNT: t('error.loanDisbursedAmount'),
		LOAN_DUPLICATE: t('error.loanDuplicate', {
			financier: cause?.details?.financier,
			num: cause?.details?.num,
		}),
		LOAN_ENTITY_DUPLICATE: t('error.loanEntityDuplicate'),
		LOAN_INVALID_STATUS: t('error.default'),
		LOAN_NON_EXISTENT: t('error.loanNonExistent'),
		LOAN_UNAUTHORIZED_GET: t('error.unauthorized'),
		LOAN_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		LOAN_UNAUTHORIZED_POST: t('error.unauthorized'),
		ME_UNAUTHORIZED_GET: t('error.unauthorized'),
		METHOD_NOT_ALLOWED: t('error.server'),
		NON_EXISTENT_CURRENCY: t('error.default'),
		PDF_FAILED: t('error.pdfFailed'),
		PLAN_UNAUTHORIZED_POST: t('error.unauthorized'),
		PRACTICE_DUPLICATE: t('error.practiceDuplicate'),
		PROGRAM_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		PROGRAM_UNAUTHORIZED_POST: t('error.unauthorized'),
		PROJECT_ALREADY_ONBOARDED: t('error.projectAlreadyOnboarded'),
		PROJECT_DUPLICATE: t('error.default'),
		PROJECT_ENTITY_NON_EXISTENT: t('error.projectEntityNonExistent'),
		PROJECT_LOAN_DUPLICATE: t('error.projectLoanDuplicate'),
		PROJECT_LOAN_NON_EXISTENT: t('error.projectLoanNonExistent'),
		PROJECT_NON_EXISTENT: t('error.default'),
		PROJECT_REPORTING_CURRENCY_NOT_FOUND: t('error.reportingCurrencyNotFound'),
		PROJECT_UNAUTHORIZED_GET: t('error.unauthorized'),
		PROJECT_UNAUTHORIZED_POST: t('error.unauthorized'),
		QUEUE_FAILURE: t('error.server'),
		RECONCILIATION_ITEM_DEFAULT_CANNOT_EDIT: t('error.default'),
		RECONCILIATION_ITEM_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		RECONCILIATION_ITEM_UNAUTHORIZED_POST: t('error.unauthorized'),
		REGION_DUPLICATE: t('error.regionDuplicate'),
		REPORT_END_DATE_BEFORE_START_DATE: t('error.reportEndDateBeforeStartDate'),
		REPORT_INVALID_TYPE: t('error.reportInvalidType'),
		TAG_NON_EXISTENT: t('error.default'),
		TAG_TRANSACTIONS_EXIST: t('error.default'),
		TAG_UNAUTHORIZED_DELETE: t('error.unauthorized'),
		TAG_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		TAG_UNAUTHORIZED_POST: t('error.unauthorized'),
		TRANSACTION_ACCOUNTS_SAME: t('error.transactionAccountsSame'),
		TRANSACTION_AMOUNT_NON_EXISTENT: t('error.transactionAmountNonExistent'),
		TRANSACTION_CATEGORY_ID_NON_EXISTENT: t('alert.categoryIdRequired'),
		TRANSACTION_CONTRACT_STEP_ID_NON_EXISTENT: t('alert.contractStepIdRequired'),
		TRANSACTION_DOCUMENTS_NON_EXISTENT: t('error.documentsNonExistent'),
		TRANSACTION_ENTITY_NOT_FOUND: t('error.default'),
		TRANSACTION_EXCEEDS_CONTRACT: t('error.transactionExceedsContract', {
			contractAmount: cause?.details?.contractAmount,
			transactionAmount: cause?.details?.transactionAmount,
		}),
		TRANSACTION_EXCEEDS_LOAN_AMOUNT: t('error.transactionExceedsLoanAmount', {
			currency: cause?.details?.currency,
			disbursedAmount: cause?.details?.disbursedAmount,
			loanAmount: cause?.details?.loanAmount,
		}),
		TRANSACTION_ID_NON_EXISTENT: t('error.transactionIdNonExistent'),
		TRANSACTION_INSUFFICIENT_FUNDS: t('error.transactionInsufficientFunds'),
		TRANSACTION_INVALID_TYPE: t('error.default'),
		TRANSACTION_NON_EXISTENT: t('error.transactionNonExistent'),
		TRANSACTION_PATCH_INCOMPLETE: t('error.transactionsPatchIncomplete'),
		TRANSACTION_RECEIVED_AMOUNT_NON_EXISTENT: t('error.transactionReceivedAmountNonExistent'),
		TRANSACTION_REIMBURSED_EXCEEDS_REIMBURSEMENT: t('error.reimbursementExceeded', {
			totalReimbursed: cause?.details?.totalReimbursed,
			totalReimbursement: cause?.details?.totalReimbursement,
		}),
		TRANSACTION_UNAUTHORIZED_GET: t('error.unauthorized'),
		TRANSACTION_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		TRANSACTION_UNAUTHORIZED_POST: t('error.unauthorized'),
		TRANSACTION_UNAUTHORIZED_RETRY: t('error.unauthorized'),
		TRANSACTION_RETRY_FAILED: t('error.transactionRetryFailed'),
		TRANSACTION_RETRY_NOT_ELIGIBLE: t('error.transactionRetryNotEligible'),
		UNKNOWN: t('error.default'),
		USER_DUPLICATE: cause?.details?.email
			? t('error.userDuplicateEmail', { email: cause?.details?.email })
			: cause?.details?.phone
				? t('error.userDuplicatePhone', { phone: cause?.details?.phone })
				: t('error.userDuplicate'),
		USER_DUPLICATE_ENTITY_PROJECT: t('error.userDuplicateEntityProject', {
			email: cause?.details?.email,
		}),
		USER_EMAIL_NON_EXISTENT: t('error.userEmailNonExistent'),
		USER_FIRST_NAME_NON_EXISTENT: t('error.userFirstNameNonExistent'),
		USER_GROUP_INVALID: t('error.userGroupInvalid'),
		USER_INVALID_ROLE: t('error.userInvalidRole'),
		USER_LAST_NAME_NON_EXISTENT: t('error.userLastNameNonExistent'),
		USER_PERMISSION_NON_EXISTENT: t('error.userPermissionNonExistent'),
		USER_PHONE_OR_EMAIL_EMPTY: t('error.userEmailOrPhoneRequired'),
		USER_UNAUTHORIZED_GET: t('error.unauthorized'),
		USER_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		USER_UNAUTHORIZED_POST: t('error.unauthorized'),
		WALLET_CREATION: t('error.server'),
		WALLET_INACTIVE: t('error.walletInactive'),
		WALLET_UPDATE: t('error.server'),
		WBG_WITHDRAWAL_APPLICATION_INTERNAL_USER: t('error.wbgWithdrawalApplicationInternalUser'),
		WBG_WITHDRAWAL_APPLICATION_POST_FAILED: t('error.wbgWithdrawalApplicationPostFailed'),
		WITHDRAWAL_APPLICATION_BORROWER_REFERENCE_NON_EXISTENT: t(
			'error.withdrawalApplicationBorrowerReferenceNonExistent'
		),
		WITHDRAWAL_APPLICATION_ERROR: t('error.withdrawalApplicationError'),
		WITHDRAWAL_APPLICATION_ID_DUPLICATE: t('error.withdrawalApplicationIdDuplicate', {
			idWbg: cause?.details?.idWbg,
		}),
		WITHDRAWAL_APPLICATION_ID_WBG_NON_EXISTENT: t('error.withdrawalApplicationIdWbgNonExistent'),
		WITHDRAWAL_APPLICATION_PROJECT_NON_EXISTENT: t('error.withdrawalApplicationProjectNonExistent'),
		WITHDRAWAL_APPLICATION_TRANSACTION_LINKED: t('error.withdrawalApplicationTransactionLinked'),
		WITHDRAWAL_APPLICATION_TRANSACTION_NON_EXISTENT: t(
			'error.withdrawalApplicationTransactionNonExistent'
		),
		WITHDRAWAL_APPLICATION_TRANSACTION_STATUS_INVALID: t(
			'error.withdrawalApplicationTransactionStatusInvalid'
		),
		WITHDRAWAL_APPLICATION_WBG_POST_FAILED: t('error.withdrawalApplicationWbgPostFailed'),
		WITHDRAWAL_APPLICATION_WBG_RESPONSE_INVALID: t('error.withdrawalApplicationWbgResponseInvalid'),
	};

	let message = t('error.default');

	if (cause?.id) {
		message = errorMap[cause.id] || message;

		if (cause.details) {
			const { currentBalance, transactionAmount } = cause.details;

			if (
				cause.id === 'TRANSACTION_INSUFFICIENT_FUNDS' &&
				currentBalance != null &&
				transactionAmount != null
			) {
				message = t('error.transactionInsufficientFundsDetails', {
					currentBalance,
					transactionAmount,
				});
			}
		}

		if (frame === 'SUPPLIER') {
			const errorMapSupplier: Partial<Record<ErrorCause['id'], string>> = {
				ENTITY_DUPLICATE: t('error.supplierDuplicate', { idExternal: cause?.details?.idExternal }),
				ENTITY_DUPLICATE_ID_EXTERNAL: t('error.supplierDuplicateIdExternal', {
					idExternal: cause?.details?.idExternal,
				}),
			};
			message = errorMapSupplier[cause.id] || message;
		}
		if (frame === 'BENEFICIARY') {
			const errorMapBeneficiary: Partial<Record<ErrorCause['id'], string>> = {
				ENTITY_DUPLICATE: t('error.beneficiaryDuplicate', {
					idExternal: cause?.details?.idExternal,
				}),
				ENTITY_DUPLICATE_ID_EXTERNAL: t('error.beneficiaryDuplicateIdExternal', {
					idExternal: cause?.details?.idExternal,
				}),
			};
			message = errorMapBeneficiary[cause.id] || message;
		}
	}

	return message;
};

export const translateFormName = (name: FormName, t: TFunction) => {
	const formMap: Record<FormName, string> = {
		BENEFICIARY_INDIVIDUAL: t('forms.beneficiaryIndividual'),
		BENEFICIARY_ORGANIZATION: t('forms.beneficiaryOrganization'),
		SUPPLIER: t('forms.supplier'),
	};

	return formMap[name] || name;
};

export const translatePermissionResource = (resource: PermissionResource, t: TFunction) => {
	const resourceMap: Record<PermissionResource, string> = {
		ACCOUNT: t('account'),
		ACT: t('activity'),
		BENEFICIARY: t('beneficiary'),
		BENEFIT: t('benefit'),
		CASH_BENEFIT: t('cashBenefit'),
		CASH_IN_METHOD: t('cashInMethod'),
		CATEGORY: t('category'),
		COMMENT: t('comment'),
		COMPONENT: t('component'),
		CONTRACT: t('contract'),
		DOCUMENT: t('document'),
		ENTITY: t('entity'),
		EXPENSE_TYPE: t('expenseType'),
		FORM: t('form'),
		INVOICE: t('invoice'),
		LOAN: t('loan'),
		NON_CASH_BENEFIT: t('nonCashBenefit'),
		OPEX: t('operatingExpense'),
		PAYMENT: t('payment'),
		PROGRAM: t('program'),
		RECONCILIATION_ITEM: t('reconciliationItem'),
		REIMBURSEMENT: t('reimbursement'),
		REPLENISHMENT: t('inflow'),
		SUPPLIER: t('supplier'),
		TAG: t('tag'),
		TRANSFER: t('transfer'),
		USER: t('user'),
		WITHDRAWAL_APPLICATION: t('withdrawalApplication'),
	};

	return resourceMap[resource] || resource;
};

export const translateRelationship = (relationship: AccountEntityRelationship, t: TFunction) => {
	const relationships: Record<AccountEntityRelationship, string> = {
		ASSOCIATE: t('associate'),
		OWNER: t('owner'),
	};

	return relationships[relationship] || relationship;
};

export const translateRole = (role: UserRole, t: TFunction) => {
	const roles: Record<UserRole, string> = {
		FNFA: t('fnfa'),
		GENERAL: t('general'),
		LIAISON: t('liaison'),
		STAFF: t('staff'),
		TTL: t('taskTeamLeader'),
		WFA: t('wfa'),
	};

	return roles[role] || role;
};

export const translateStatus = (status: Status, t: TFunction) => {
	const statuses: Record<Status, string> = {
		ACTIVE: t('active'),
		APPROVED: t('approved'),
		COMPLETED: t('completed'),
		CREATED: t('created'),
		DEACTIVATED: t('deactivated'),
		DELETED: t('deleted'),
		DISABLED: t('disabled'),
		DONE: t('done'),
		DRAFT: t('draft'),
		ENABLED: t('enabled'),
		ERROR: t('error'),
		FAILED: t('failed'),
		FINALIZED: t('finalized'),
		HIGH: t('high'),
		INACTIVE: t('inactive'),
		INDETERMINATE: t('indeterminate'),
		INITIATED: t('initiated'),
		INCOMPLETE: t('incomplete'),
		INVITED: t('invited'),
		IN_PROGRESS: t('inProgress'),
		LOW: t('low'),
		MINIMAL: t('minimal'),
		MODERATE: t('moderate'),
		ON_HOLD: t('onHold'),
		PAID: t('paid'),
		PAYMENT_IN_PROGRESS: t('paymentInProgress'),
		PENDING: t('pending'),
		PENDING_REVIEW: t('pendingReview'),
		READY: t('ready'),
		READY_FOR_PAYMENT: t('readyForPayment'),
		REJECTED: t('rejected'),
		RETRY: t('retry'),
		REVERTED: t('reverted'),
		REVISION: t('revision'),
		STARTED: t('started'),
		SUBMITTED: t('submitted'),
		TRIAL: t('trial'),
	};

	return statuses[status] || status;
};

export const translateStatusDescription = (status: TransactionStatus, t: TFunction) => {
	const statusDescriptions: Record<TransactionStatus, string> = {
		COMPLETED: t('status.transaction.completed.description'),
		FAILED: t('status.transaction.failed.description'),
		INITIATED: t('status.transaction.initiated.description'),
		INCOMPLETE: t('status.transaction.incomplete.description'),
		IN_PROGRESS: t('status.transaction.inProgress.description'),
		PENDING: t('status.transaction.pending.description'),
		REVERTED: t('status.transaction.reverted.description'),
	};

	return statusDescriptions[status] || status;
};

export interface TranslateStatusTooltipProps {
	status: Status;
	transaction?: {
		type?: TransactionType;
	};
	t: TFunction;
}

export const translateStatusTooltip = ({ status, t, transaction }: TranslateStatusTooltipProps) => {
	const statusTooltips: Partial<Record<Status, string>> = {
		COMPLETED: t('status.transaction.completed.tooltip'),
		FAILED: t('status.transaction.failed.tooltip'),
		INITIATED: t('status.transaction.initiated.tooltip'),
		INCOMPLETE: t('status.transaction.incomplete.tooltip'),
		IN_PROGRESS: t('status.transaction.inProgress.tooltip'),
		PENDING: t('status.transaction.pending.tooltip'),
		REVERTED: t('status.transaction.reverted.tooltip'),
		REVISION: t('status.ifrPlan.revision.tooltip'),
	};

	return statusTooltips[status] || status;
};

export const translateTransactionType = (type: TransactionType, t: TFunction) => {
	const types: Record<TransactionType, string> = {
		ADVANCE: t('advance'),
		CASH_BENEFIT: t('cashBenefit'),
		DIRECT: t('directPayment'),
		INITIAL: t('initialDeposit'),
		OPEX: t('operatingExpense'),
		NON_CASH_BENEFIT: t('nonCashBenefit'),
		PAYMENT: t('payment'),
		PAYMENT_REFUND: t('paymentRefund'),
		REIMBURSEMENT: t('reimbursement'),
		REPLENISHMENT: t('inflow'),
		SPECIAL: t('specialCommitment'),
		TRANSFER: t('transfer'),
	};

	return types[type] || type;
};

export const translateEntityType = (type: EntityType | EntitySubtype, t: TFunction) => {
	const types: Record<EntityType | EntitySubtype, string> = {
		AUDITOR: t('auditor'),
		BENEFICIARY: t('beneficiary'),
		BORROWER: t('borrower'),
		DONOR: t('donor'),
		INDIVIDUAL: t('individual'),
		INTERMEDIARY: t('intermediary'),
		ORGANIZATION: t('organization'),
		PIU: t('piu'),
		SUB_PIU: t('subPiu'),
		SUPPLIER: t('supplier'),
		TAX_AUTHORITY: t('taxAuthority'),
	};

	return types[type] || type;
};

export const translateLanguage = (languageCode: Intl.Locale['language']) => {
	const languages: Record<Intl.Locale['language'], string> = {
		af: 'Afrikaans',
		am: 'አማርኛ',
		ar: 'العربية',
		az: 'Azərbaycan',
		be: 'Беларуская',
		bg: 'Български',
		bn: 'বাংলা',
		bs: 'Bosanski',
		ca: 'Català',
		cs: 'Čeština',
		cy: 'Cymraeg',
		da: 'Dansk',
		de: 'Deutsch',
		el: 'Ελληνικά',
		en: 'English',
		es: 'Español',
		et: 'Eesti',
		eu: 'Euskera',
		fa: 'فارسی',
		fi: 'Suomi',
		fil: 'Filipino',
		fr: 'Français',
		ga: 'Gaeilge',
		gl: 'Galego',
		gu: 'ગુજરાતી',
		he: 'עברית',
		hi: 'हिन्दी',
		hr: 'Hrvatski',
		hu: 'Magyar',
		hy: 'Հայերեն',
		id: 'Bahasa Indonesia',
		is: 'Íslenska',
		it: 'Italiano',
		ja: '日本語',
		ka: 'ქართული',
		kk: 'Қазақ тілі',
		km: 'ខ្មែរ',
		kn: 'ಕನ್ನಡ',
		ko: '한국어',
		ky: 'Кыргызча',
		lo: 'ລາວ',
		lt: 'Lietuvių',
		lv: 'Latviešu',
		mk: 'Македонски',
		ml: 'മലയാളം',
		mn: 'Монгол',
		mr: 'मराठी',
		ms: 'Bahasa Melayu',
		mt: 'Malti',
		my: 'မြန်မာ',
		nb: 'Norsk bokmål',
		ne: 'नेपाली',
		nl: 'Nederlands',
		nn: 'Norsk nynorsk',
		or: 'ଓଡ଼ିଆ',
		pa: 'ਪੰਜਾਬੀ',
		pl: 'Polski',
		ps: 'پښتو',
		pt: 'Português',
		ro: 'Română',
		ru: 'Русский',
		si: 'සිංහල',
		sk: 'Slovenčina',
		sl: 'Slovenščina',
		sq: 'Shqip',
		sr: 'Српски',
		sv: 'Svenska',
		sw: 'Kiswahili',
		ta: 'தமிழ்',
		te: 'తెలుగు',
		th: 'ไทย',
		tr: 'Türkçe',
		uk: 'Українська',
		ur: 'اردو',
		uz: "O'zbek",
		vi: 'Tiếng Việt',
		zh: '中文（简体）',
		zu: 'IsiZulu',
	};

	return languages[languageCode] || languageCode;
};

export const translateAccountType = (type: string, t: TFunction) => {
	const accountTypeMap: Record<string, string> = {
		DESIGNATED: t('designated'),
		LOCAL: t('local'),
	};

	return accountTypeMap[type] || type;
};
export const translateCategory = (category: ArticleCategory, t: TFunction): string => {
	const categoryMap: Record<ArticleCategory, string> = {
		ACCOUNTS: t('category.accounts'),
		ENTITIES: t('category.entities'),
		PROJECTS: t('category.projects'),
		REPORTS: t('category.reports'),
		SETTINGS: t('category.settings'),
		TRANSACTIONS: t('category.transactions'),
	};

	return categoryMap[category] || category;
};
